import { FC, useState, useEffect } from "react";
import { motion } from "framer-motion";

import { FlexContainer, Icon, MutedText } from "shared/styles";
import { ILocalStorageKey, ITheme } from "shared/types";
import { Card, Button } from "shared/components";
import { Spacer } from "shared/layout";

import { copy } from "shared/assets";
import { LocalStorageService } from "shared/services";
import { fadeIn } from "shared/styles/constants";
import { FancyButtonLink, FancyLink } from "shared/components";

interface EditBlockProps {
  localStorageKey?: ILocalStorageKey;
  items: IEditBlockItem[];
  id: string;
  cardTitle: string;
  cardIcon: React.ComponentType<any>;
  cardAccentColor?: string;
}

export interface IEditBlockItem {
  icon: React.ComponentType<any>;
  to?: string;
  handleClick?: () => void;
  label: string;
  subLabel?: string;
}

export const EditBlock: FC<EditBlockProps> = ({
  items,
  localStorageKey,
  id,
  cardTitle,
  cardIcon,
  cardAccentColor,
}) => {
  const [hideBlocks, setHideBlocks] = useState(
    localStorageKey
      ? (LocalStorageService.getItem(localStorageKey) as string[]) || []
      : []
  );
  const [hideBlock, setHideBlock] = useState(false);

  useEffect(() => {
    setHideBlock(false);

    if (hideBlocks?.includes(id)) {
      setHideBlock(true);
    }
  }, [id, hideBlocks]);

  const onHideBlock = () => {
    if (localStorageKey) {
      setHideBlock(true);
      const newHideBlocks = [...hideBlocks, id];

      setHideBlocks(newHideBlocks);
      LocalStorageService.setItem(localStorageKey, newHideBlocks);
    }
  };

  if (hideBlock) return null;

  return (
    <motion.div
      {...fadeIn}
      initial={{ opacity: 1, y: 0 }}
      data-testid="edit_block"
    >
      <Card title={cardTitle} icon={cardIcon} accentColor={cardAccentColor}>
        <Spacer size="xl" as="ul">
          {items.map(({ icon, to, handleClick, label, subLabel }, index) => (
            <li key={index}>
              <FlexContainer align="center">
                <Icon as={icon} size="xxl" />
                {to && <FancyLink to={to}>{label}</FancyLink>}
                {handleClick && (
                  <FancyButtonLink onClick={handleClick}>
                    {label}
                  </FancyButtonLink>
                )}
                {subLabel && <MutedText>{subLabel}</MutedText>}
              </FlexContainer>
            </li>
          ))}
          {localStorageKey && (
            <Button
              theme={ITheme.secondary}
              onClick={onHideBlock}
              data-testid="edit_block_hide"
            >
              {copy.actions.hide}
            </Button>
          )}
        </Spacer>
      </Card>
    </motion.div>
  );
};
